import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { profileService } from '@/services/api/profile.service';
import type {
  ProfileUpdateRequest,
  EmailChangeRequest,
  EmailChangeVerification,
  PasswordResetRequest,
  PasswordResetVerification
} from '@/types/profile';

/**
 * Hook for updating user profile (name, phone)
 */
export function useProfileUpdate() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('profile');

  return useMutation({
    mutationFn: (data: ProfileUpdateRequest) => profileService.updateProfile(data),
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      toast({
        title: t('toasts.profileUpdated'),
        description: t('toasts.profileUpdatedDesc')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.profileUpdateFailed'),
        description: error.response?.data?.message || error.message || t('toasts.profileUpdateFailedDesc'),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook for email change flow
 */
export function useEmailChange() {
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('profile');

  const requestMutation = useMutation({
    mutationFn: (data: EmailChangeRequest) => profileService.requestEmailChange(data),
    onSuccess: (response) => {
      toast({
        title: t('toasts.verificationCodeSent'),
        description: response.message || t('toasts.verificationCodeSentDesc')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.verificationCodeFailed'),
        description: error.response?.data?.message || error.message || t('toasts.verificationCodeFailedDesc'),
        variant: 'destructive'
      });
    }
  });

  const verifyMutation = useMutation({
    mutationFn: (data: EmailChangeVerification) => profileService.verifyEmailChange(data),
    onSuccess: async () => {
      await refreshUser();
      toast({
        title: t('toasts.emailUpdated'),
        description: t('toasts.emailUpdatedDesc')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.emailVerificationFailed'),
        description: error.response?.data?.message || error.message || t('toasts.emailVerificationFailedDesc'),
        variant: 'destructive'
      });
    }
  });

  return {
    requestEmailChange: requestMutation.mutateAsync,
    verifyEmailChange: verifyMutation.mutateAsync,
    isRequesting: requestMutation.isPending,
    isVerifying: verifyMutation.isPending,
    requestError: requestMutation.error,
    verifyError: verifyMutation.error
  };
}

/**
 * Hook for password reset flow
 */
export function usePasswordReset() {
  const { toast } = useToast();
  const { t } = useTranslation('profile');

  const requestMutation = useMutation({
    mutationFn: (data: PasswordResetRequest) => profileService.requestPasswordReset(data),
    onSuccess: (response) => {
      toast({
        title: t('toasts.resetEmailSent'),
        description: t('toasts.resetEmailSentDesc')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.resetEmailFailed'),
        description: error.response?.data?.message || error.message || t('toasts.resetEmailFailedDesc'),
        variant: 'destructive'
      });
    }
  });

  const resetMutation = useMutation({
    mutationFn: (data: PasswordResetVerification) => profileService.resetPassword(data),
    onSuccess: (response) => {
      toast({
        title: t('toasts.passwordResetSuccess'),
        description: t('toasts.passwordResetSuccessDesc')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.passwordResetFailed'),
        description: error.response?.data?.message || error.message || t('toasts.passwordResetFailedDesc'),
        variant: 'destructive'
      });
    }
  });

  return {
    requestPasswordReset: requestMutation.mutateAsync,
    resetPassword: resetMutation.mutateAsync,
    isRequesting: requestMutation.isPending,
    isResetting: resetMutation.isPending,
    requestSuccess: requestMutation.isSuccess,
    resetSuccess: resetMutation.isSuccess
  };
}
